/**
 * safeInvoke — drop-in replacement for supabase.functions.invoke() that
 * bypasses the broken getSession() path by reading the access token
 * directly from localStorage and attaching it manually.
 *
 * Spec: docs/superpowers/specs/2026-05-17-safe-invoke-wrapper-design.md
 * Registry: supabase_get_session_root_cause_fix
 *
 * Browser-only by design. Asserted at module load.
 */

// Browser-only — fail fast at import time if loaded in an SSR/Node context.
// safeInvoke depends on localStorage and is intended for client-side use only.
if (typeof window === "undefined") {
  const err = new Error("safeInvoke is browser-only");
  err.name = "SafeInvokeError";
  throw err;
}

export interface StoredSession {
  access_token?: string;
  refresh_token?: string;
  expires_at?: number;
}

export interface SafeInvokeOptions {
  body?: unknown;
}

export class SafeInvokeError extends Error {
  status?: number;
  body?: string;
  cause?: unknown;

  constructor(
    message: string,
    opts?: { status?: number; body?: string; cause?: unknown },
  ) {
    super(message);
    // Override Error.prototype.name; mutable so TS strict-override configs
    // (noImplicitOverride) don't require the `override` keyword here.
    this.name = "SafeInvokeError";
    this.status = opts?.status;
    this.body = opts?.body;
    this.cause = opts?.cause;
    // Maintain proper prototype chain for instanceof under transpilation.
    Object.setPrototypeOf(this, SafeInvokeError.prototype);
  }
}

const PRIMARY_STORAGE_KEY = "nexus-auth-token";

function deriveDefaultStorageKey(): string | null {
  const url = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim();
  if (!url) return null;
  // Parse project ref from https://<ref>.supabase.co (with or without trailing slash/path)
  const match = url.match(/^https?:\/\/([^.]+)\.supabase\.co/);
  if (!match) return null;
  return `sb-${match[1]}-auth-token`;
}

export function functionsUrl(name: string): string {
  const url = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim();
  if (!url) {
    throw new SafeInvokeError("VITE_SUPABASE_URL is not set");
  }
  const normalized = url.replace(/\/+$/, "");
  return `${normalized}/functions/v1/${name}`;
}

export function getAccessToken(): string {
  const candidates: string[] = [PRIMARY_STORAGE_KEY];
  const fallback = deriveDefaultStorageKey();
  if (fallback !== null && fallback !== PRIMARY_STORAGE_KEY) {
    candidates.push(fallback);
  }

  let raw: string | null = null;
  for (const key of candidates) {
    const value = localStorage.getItem(key);
    if (value !== null) {
      raw = value;
      break;
    }
  }

  if (raw === null) {
    throw new SafeInvokeError("No supabase auth token in storage");
  }

  let parsed: StoredSession;
  try {
    parsed = JSON.parse(raw) as StoredSession;
  } catch (cause) {
    throw new SafeInvokeError("Malformed auth session in storage", { cause });
  }

  if (
    typeof parsed.access_token !== "string" ||
    parsed.access_token.length === 0
  ) {
    throw new SafeInvokeError("No access_token in stored session");
  }

  return parsed.access_token;
}

/**
 * Drop-in replacement for `supabase.functions.invoke<T>(name, { body })`.
 * Reads the access token from localStorage and attaches it as a Bearer
 * header. Returns the same `{ data, error }` envelope as supabase-js.
 *
 * On 401, returns the error as-is — no auto-refresh, no retry. Auth
 * recovery is application policy.
 */
export async function safeInvoke<T = unknown>(
  name: string,
  options?: SafeInvokeOptions,
): Promise<{ data: T | null; error: SafeInvokeError | null }> {
  let token: string;
  try {
    token = getAccessToken();
  } catch (err) {
    return { data: null, error: err as SafeInvokeError };
  }

  let url: string;
  try {
    url = functionsUrl(name);
  } catch (err) {
    return { data: null, error: err as SafeInvokeError };
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body:
        options?.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  } catch (cause) {
    return {
      data: null,
      error: new SafeInvokeError(`Network error invoking ${name}`, { cause }),
    };
  }

  if (!res.ok) {
    const bodyText = await res.text().catch(() => "");
    return {
      data: null,
      error: new SafeInvokeError(
        `Edge function ${name} returned HTTP ${res.status}`,
        { status: res.status, body: bodyText },
      ),
    };
  }

  try {
    const data = (await res.json()) as T;
    return { data, error: null };
  } catch (cause) {
    return {
      data: null,
      error: new SafeInvokeError(
        `Edge function ${name} returned invalid JSON`,
        { cause },
      ),
    };
  }
}
